/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../FiltersAPI',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./LocalFiltersAPISpec',
	'../../filters/VisFilterProxy',
	'../../visualizations/renderer/filter/FilterLabelHelper',
	'../../filters/FilterSpecHelper'
], (_, FiltersAPI, APIFactory, LocalFiltersAPISpec, FilterProxy, FilterLabelHelper, FilterSpecHelper) => {
	class LocalFiltersAPIImpl extends LocalFiltersAPISpec {

		/**
		 * Enumeration of filter condition operators
		 * @public
		 * @enum
		 */
		static get CONDITIONS_OPERATORS() {
			return {
				EQUALS: 'in',
				CONTAINS: 'containsignorecase',
				STARTS_WITH: 'beginswith',
				ENDS_WITH: 'endswith'
			};
		}

		constructor (options = {}) {
			super(options);
			this.visualization = options.visualization;
			this.dataModel = options.dataModel;
			this.filterSupport = options.filterSupport;
			this.transaction = options.transaction;
			this._localFilters = this.filterSupport.getLocalFilters();
			this._proxy = new FilterProxy({
				input: this._localFilters,
				output: this._localFilters
			});
			this.api = APIFactory.createAPI(this, [FiltersAPI]);
		}

		getAPI(){
			return this.api;
		}

		destroy() {
			this.visualization = null;
			this.filterSupport = null;
			this._localFilters = null;
			this.transaction = null;
			this._proxy = null;
		}

		/**
		 * Get the list of filter UI descriptions
		 * @public
		 * @function LocalFiltersAPI#getFiltersDesc
		 * @returns {Object[]} array of filter descriptions
		 */
		getFiltersDesc() {
			const filterLabelHelper = new FilterLabelHelper({ 'dataSource' : this.visualization.getDataSource(), 'dashboardApi': this.filterSupport.dashboardApi });
			const filters = this.filterSupport.getFilterInfo();
			if (filters && filters.localFilters.length > 0) {
				return filterLabelHelper.generateFilterListItems(filters.localFilters);
			}
			return [];
		}

		/**
		 * @function LocalFiltersAPI#getFilterList
		 * @description Get the list of local filter specifications
		 * @returns {Object[]} array of local filter specifications
		 */
		getFilterList() {
			const filterSpecs = [];
			if (this._localFilters) {
				this._localFilters.models.forEach((filterEntry) => {
					const filterEntrySpec = filterEntry.toJSON(['isMissing']);
					// assign computed property for filter spec
					filterEntrySpec.readOnly = !this.filterSupport.isFilterEditable(filterEntry);
					filterSpecs.push(filterEntrySpec);
				});
			}
			return filterSpecs;
		}

		/**
		 * Check whether a local filter for a data item exists or not
		 * @public
		 * @function LocalFiltersAPI#filterExists
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [item.aggregationType] data item aggregation type
		 * @returns {boolean} true if a local filter for the data item exists, otherwise false
		 */
		filterExists(itemContext) {
			const key = this._getKey(itemContext);
			return !!this._localFilters.getFilterEntry(key);
		}

		_setOptionToLegacyOptions(setOptions = {}){
			const payloadData = setOptions && setOptions.payloadData || {};
			payloadData.undoRedoTransactionId = payloadData.undoRedoTransactionId || (setOptions && setOptions.transactionId) || _.uniqueId('_updateFilter');
			return { payloadData: payloadData };
		}

		/**
		 * Add a new local filter to the widget
		 * @public
		 * @async
		 * @function LocalFiltersAPI#addFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] data item aggregation type
		 * @param {string} [itemContext.filterType] data item of type 'filter'
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @param {Object} [filterContext.conditionsInfo] filter conditions information
		 * @param {Object} [filterContext.conditionsInfo.conditions] filter conditions
		 * @param {Object} [filterContext.conditionsInfo.conditions.operator = 'containsignorecase'] filter condition operator
		 * @param {Object} [filterContext.conditionsInfo.conditions.values] filter condition values
		 * @param {string[]} [filterContext.conditionsInfo.excludedValues = []]
		 * @returns {string} An object with an id (id could be undefined if no entry was added).
		 * @example
		 *	filters.addFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}]
		 *	});
		 *
		 *	filters.addFilter({
		 *		itemId: 'sales_and_marketing_csv.Quantity'
		 *	}, {
		 *		from: {
		 *			u: 0,
		 *			d: 0
		 *		},
		 *		to: {
		 *			u: 500000,
		 *			d: 500000
	 	 *		}
		 *	});
		 */
		addFilter(itemContext, filterContext, transactionToken) {
			_.defaults(filterContext, {
				action: 'keepexclude',
				exclude: false
			});

			const filterEntry = this._updateFilter(itemContext, filterContext, transactionToken);
			return { id: filterEntry && filterEntry.id };
		}

		/**
		 * Edit an existing filter
		 * @public
		 * @async
		 * @function LocalFiltersAPI#editFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] data item aggregation type
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @example
		 *	filters.editFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}]
		 *	});
		 */
		editFilter(itemContext, filterContext, transactionToken) {
			// ensure the filter already exists
			if (!this.filterExists(itemContext)) {
				return;
			}

			this._updateFilter(itemContext, filterContext, transactionToken);
		}

		/**
		 * Remove an existing filter
		 * @public
		 * @async
		 * @function LocalFiltersAPI#removeFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [item.aggregationType] data item aggregation type
		 * @example
		 *	filters.removeFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	});
		 */
		removeFilter(itemContext, transactionToken) {
			const options = this._setOptionToLegacyOptions(transactionToken);
			options.isApiUndoRedoEvent = true;
			let key;
			if (itemContext.id) {
				key = { id : itemContext.id };
			} else if (this.filterExists(itemContext)) {
				key = this._getKey(itemContext);
			}

			if (key && this._localFilters.removeFilterEntry(key)) {
				const isEmptyViz = this.visualization.getSlots().getDataItemList().length === 0 && this.getFilterList().length === 0;
				if (isEmptyViz) {
					this.dataModel.removeDataView(null, /**Remove all dataViews*/true);
				}
				this._localFilters.allFilterModificationComplete(options);
				this._onAfterFilterModification();
				return true;
			}
			return false;
		}

		_onAfterFilterModification() {
			this.filterSupport.updateMissingFilters();
		}

		_getKey (itemContext) {
			if (itemContext.id) {
				return itemContext;	//Already have the id in the itemContext.  Don't need to compute it!
			}
			if (itemContext.itemIds) {
				return {
					id: this._localFilters.getDataPointFilterKey(itemContext.itemIds)
				};
			}
			const filterType = itemContext.aggregationType ? this._localFilters.FILTERTYPE_ORDINAL : this._localFilters.FILTERTYPE_CATEGORY;
			return this._localFilters.getKey(itemContext.itemId, filterType, itemContext.aggregationType);
		}

		_updateFilter(itemContext, filterContext, transactionToken) {

			const options = this._setOptionToLegacyOptions(transactionToken);
			options.isApiUndoRedoEvent = true;

			if (filterContext.from || filterContext.to) {
				const result = this._proxy.updateRangeFilter(itemContext, filterContext, options);
				this._onAfterFilterModification();
				return result;
			}

			let result = null;
			if (!itemContext.itemId && _.isArray(itemContext.itemIds)) {
				result = this._proxy.addDataPoints(itemContext, filterContext, options);
			} else {
				//supply defaults to the conditions in case the itemId, operator, or valueType is omitted. This handles the case where
				//there are multiple conditions (and, or)
				if (filterContext.conditionsInfo && filterContext.conditionsInfo.conditions) {
					this._updateFilterDefaults(itemContext, filterContext.conditionsInfo.conditions);
				}

				result = this._proxy.updateFilter(itemContext, filterContext, options);
				this._onAfterFilterModification();
			}
			return result;
		}

		_updateFilterDefaults(itemContext, conditions) {
			const compound = conditions.and || conditions.or;
			if (compound) {
				_.each(compound, (condition) => {
					this._updateFilterDefaults(itemContext, condition);
				});
			} else {
				if (conditions.not) {
					this._defaultConditions(itemContext, conditions.not);
				} else {
					this._defaultConditions(itemContext, conditions);
				}
			}
		}

		_defaultConditions(itemContext, conditions) {
			_.defaults(conditions, {
				itemId: itemContext.itemId,
				operator: LocalFiltersAPIImpl.CONDITIONS_OPERATORS.CONTAINS,
				valueType: 'display'
			});
		}

		_getCurrentFilterContextByFilterId(itemContext) {
			return this.getFilterList().find(filter => filter.id === itemContext.id);
		}

		_getCurrentFilterContext(itemContext) {
			// There could be multiple filters having the same itemContext itemId.
			let currentFilter = this._getCurrentFilterContextByFilterId(itemContext);
			if(_.isEmpty(currentFilter)) {
				currentFilter = this.getFilterList().find(filter => filter.columnId === itemContext.itemId || filter.id === itemContext.itemId) || {};
			}

			if (this._getExtraFilterAttributes(itemContext).isDatapointFilter) {
				return this._datapointFilterSpecToFilterContext(currentFilter);
			}

			if (currentFilter.conditions) {
				let conditionsInfo = {
					conditions: currentFilter.conditions,
					excludedValues: currentFilter.excludedValues
				};
				delete currentFilter.conditions;
				delete currentFilter.excludedValues;
				currentFilter.conditionsInfo = conditionsInfo;
			}

			if (currentFilter.operator && currentFilter.operator.indexOf('not') >= 0) {
				currentFilter.exclude = true;
			}

			if (FilterSpecHelper.isRange(currentFilter)) {
				let from, to;
				switch(currentFilter.operator) {
					case 'between':
					case 'notbetween':
						from = currentFilter.values[0].u;
						to = currentFilter.values[1].u;
						break;
					case 'gt':
						from = currentFilter.values[0].u;
						to = '';
						break;
					case 'lt':
						from = '';
						to = currentFilter.values[0].u;
				}
				_.defaults(currentFilter, { from, to });
			}
			const isAllMembersOfTheSetToggled = currentFilter.valueDataItem && currentFilter.values && currentFilter.values.length === 0;
			if(isAllMembersOfTheSetToggled) {
				delete currentFilter.values;
			}
			return currentFilter;
		}

		_getCurrentItemContext(itemContext) {
			//Augment the simple item context passed in with additional information needed to add a filter.
			const extraFilterAttributes = this._getExtraFilterAttributes(itemContext);

			let currentItemContext = JSON.parse(JSON.stringify(itemContext));	//Clone the itemContext.
			if (extraFilterAttributes.isDatapointFilter) {
				//There should be no itemId specified in a datapoint filter.  The itemContext should contain an array of itemId's.
				currentItemContext.itemIds = extraFilterAttributes.columnIds;
				delete currentItemContext.itemId;	//Shouldn't need to do this but this guarantees we don't have an itemId in context for datapoint filters.
			} else if (!currentItemContext.itemId && extraFilterAttributes.columnId) {
				//If we don't have itemId in the itemContext, add it from the filter entry (that we found by id).
				//Its needed to add the filter back.
				currentItemContext.itemId = extraFilterAttributes.columnId;
			}
			//filterType in itemContext is important for undo/redo so that addFilter creates a modelFilter type.
			//(not sure why this filterType isn't in the spec!.  FilterEntry computes it as no operator, no value and no values)
			if (extraFilterAttributes.isModelFilter) {
				currentItemContext.filterType = 'filter';
			}
			return currentItemContext;
		}

		//Collect some information about the filter.  This COULD be obtained from getFilterList() but would require
		//searching the object which seems like more work than necessray.  Instead, we should investigate simplifying the JSON format of filters
		//to make it more like the filterContext form passed into addFilter.
		_getExtraFilterAttributes(itemContext) {
			const filterAttributes = {
				isDatapointFilter: false,
				isModelFilter: false
			};
			if (this._localFilters) {
				//Get some optional information that isn't readily available in the filterSpec.
				const localFilterEntry = this._localFilters.get(this._getKey(itemContext).id);
				if (localFilterEntry) {
					if (localFilterEntry.isCompound()) {
						filterAttributes.isDatapointFilter = true;
						filterAttributes.columnIds = localFilterEntry.getColumnIds();
					} else {
						filterAttributes.columnId = localFilterEntry.columnId;
					}
					filterAttributes.isModelFilter = localFilterEntry.isModelFilter;
				}
			}
			return filterAttributes;
		}

		/**
		 * @description Convert a JSON filter specification (as returned by getFilterList() and persisted in the WidgetSpec)
		 * 				to a filterContext which is compatible with the addFilter API.  This function is used to support undo/redo of deleting a datapoint filter.
		 * @param {Object} datapointFilterSpec - The filterSpec for a datapoint filter as returned in an entry of the getFilterList() API
		 *	eg: (simplified)
		 * 		{ operator: 'not', values: [	<== optional for exclude
		 *			{ operator: 'or', values: [								<== optional (but currently always there
		 *				{ operator: 'and', values: [tuplePart, tuplePart, ...] },		<== one per dataPoint.
		 *				{ operator: 'and', values: [tuplePart, tuplePart, ...] }
		 *			]}
		 *		]}
		 *
		 * @returns {Object} a filtercontext compatible with the addFilter API.
		 *	{
		 *		exclude: true, 						<== optional for exclude datapoints
		 *		values: [
		 *			[tuplePart, tuplePart],			<== note tupleParts in the filterContext have values flattened as .d and .u members
		 *			[tuplePart, tuplePart]
		 *		]
		 *	}
		 *
		 * NOTE: We should investigate simplifying the getFilterList() form of datapoint filters to make it more like filterContext.
		 */
		_datapointFilterSpecToFilterContext(datapointFilterSpec) {
			const filterContext={};
			let unsupportedError=false;
			//Process a single tuple inside an 'and' operator as returned in a getFilterList() entry.
			//Example:
			//{ columnId: 'c1', etc., values: [ { u: []->2007, d: 2007 } ]  becomes
			//{ columnId: 'c1', etc., u: []->2007, d: 2007 }
			const tupleSpecToFilterContext = (dpFilterSpecTuple) => {
				const filterContextTuple=[];
				dpFilterSpecTuple.forEach((tuplePart) => {
					const filterContextTuplePart = JSON.parse(JSON.stringify(tuplePart));
					filterContextTuple.push(filterContextTuplePart);

					//Its an array but we only expect 1 entry in it for datapoint filters.
					tuplePart.values.forEach((tuplePartValue) => {
						if (tuplePartValue.u) {
							filterContextTuplePart.u = tuplePartValue.u;
						}
						if (tuplePartValue.d) {
							filterContextTuplePart.d = tuplePartValue.d;
						}
					});

					delete filterContextTuplePart.values;
				});
				return filterContextTuple;
			};

			//Process the arguments of a 'not', 'or' or 'and' operator in a getFilterList() entry.
			const operatorSpecToFilterContext = (dpOperatorValues) => {
				dpOperatorValues.forEach((dpOperatorValue) => {
					if (dpOperatorValue.operator==='or') {
						operatorSpecToFilterContext(dpOperatorValue.values || []);
					} else if (dpOperatorValue.operator==='and') {
						if (filterContext.values) {
							filterContext.values.push(tupleSpecToFilterContext(dpOperatorValue.values));
						} else {
							filterContext.values = [tupleSpecToFilterContext(dpOperatorValue.values)];
						}
					} else {
						unsupportedError=true;	//Not a dataPoint filter form we support??
					}
				});
			};

			//Process the "root" of a datapoint filter spec as returned in a getFilterList() entry and call sub-functions to process the parts.
			if (datapointFilterSpec.operator==='not' || datapointFilterSpec.operator==='or') {
				filterContext.exclude = !!(datapointFilterSpec.operator==='not');
				operatorSpecToFilterContext(datapointFilterSpec.values);
			} else if (datapointFilterSpec.operator==='and') {
				filterContext.values = [tupleSpecToFilterContext(datapointFilterSpec.values)];	//Usually the 'and' has an 'or' over it. This form isn't likely used.
			} else {
				unsupportedError=true;
			}

			//TODO: probably should log this.
			return unsupportedError ? null : filterContext;
		}
	}

	return LocalFiltersAPIImpl;
});
